import { ESubjects, type EActions } from "@/api/models/auth/ability";
import { useAbility } from "@casl/vue";
import { AppError } from "@/utils/AppError";
import { computed, toValue, type MaybeRefOrGetter } from "vue";

/**
 * Create permission matrix for multiple subjects and actions.
 * Returns a computed map so checks re-evaluate when CASL ability rules change
 * (e.g. after impersonation via ability.update()).
 *
 * @param action - Single action or array of actions
 * @param subject - Single subject or array of subjects
 * @param keys - Optional array of keys. If not provided, keys are generated from subjects
 * @returns Computed object with permission checks for each key
 */
export const usePermissionMatrix = (
  action: EActions | EActions[],
  subject: ESubjects | ESubjects[],
  keys?: string[]
) => {
  const { can } = useAbility();

  // Generate keys from subjects if not provided
  const generatedKeys =
    keys ||
    (Array.isArray(subject)
      ? subject.map((sub) => sub.split(".").pop() || sub)
      : [subject.split(".").pop() || subject]);

  const isActionArray = Array.isArray(action);
  if (isActionArray && action.length !== generatedKeys.length) {
    throw new AppError("Action array length must match keys array length", {
      context: {
        feature: "permissionHelpers",
        reason: "action-length-mismatch",
        actionLength: action.length,
        keysLength: generatedKeys.length,
      },
    });
  }
  const isSubjectArray = Array.isArray(subject);
  if (isSubjectArray && subject.length !== generatedKeys.length) {
    throw new AppError("Subject array length must match keys array length", {
      context: {
        feature: "permissionHelpers",
        reason: "subject-length-mismatch",
        subjectLength: subject.length,
        keysLength: generatedKeys.length,
      },
    });
  }

  return computed(() =>
    Object.fromEntries(
      generatedKeys.map((key, index) => [
        key,
        can(isActionArray ? action[index]! : action, isSubjectArray ? subject[index]! : subject),
      ])
    )
  );
};

// Backward compatibility alias
export const useGenericPermissions = usePermissionMatrix;

/**
 * Check if user has any of the specified permissions
 * @param permissions - Object (or ref/getter) with permission checks
 * @returns Computed boolean indicating if user has any permission
 */
export const useHasAnyPermission = (permissions: MaybeRefOrGetter<Record<string, boolean>>) => {
  return computed(() => Object.values(toValue(permissions)).some(Boolean));
};

/**
 * Check if user has all of the specified permissions
 * @param permissions - Object (or ref/getter) with permission checks
 * @returns Computed boolean indicating if user has all permissions
 */
export const useHasAllPermissions = (permissions: MaybeRefOrGetter<Record<string, boolean>>) => {
  return computed(() => Object.values(toValue(permissions)).every(Boolean));
};

/**
 * Filter permissions to only include those that are true
 * @param permissions - Object (or ref/getter) with permission checks
 * @returns Computed object with only true permissions
 */
export const useFilterGrantedPermissions = (
  permissions: MaybeRefOrGetter<Record<string, boolean>>
) => {
  return computed(() =>
    Object.fromEntries(Object.entries(toValue(permissions)).filter(([, value]) => value))
  );
};
